import * as Sentry from "@sentry/react";

const dsn = process.env.REACT_APP_SENTRY_DSN;
// Auto-detect environment from the host so the same build tags preview vs
// production correctly (REACT_APP_SENTRY_ENVIRONMENT overrides if set).
const detectedEnv =
  typeof window !== "undefined" && /preview|localhost|127\.0\.0\.1/.test(window.location.hostname)
    ? "preview"
    : "production";
const environment = process.env.REACT_APP_SENTRY_ENVIRONMENT || detectedEnv;
const release = process.env.REACT_APP_SENTRY_RELEASE || "drawready@web";

// Financial app: never collect PII by default; scrub sensitive fields before send.
const SENSITIVE = /password|passwd|token|secret|authorization|cookie|session|ssn|tax.?id|account.?number|routing.?number|credit.?score|income|loan.?amount|date.?of.?birth|dob|email|phone|address|api.?key|recaptcha/i;

if (dsn) {
  Sentry.init({
    dsn,
    environment,
    release,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: environment === "production" ? 0.1 : 0.0,
    sendDefaultPii: false,
    // Drop noise that isn't a DrawReady bug: browser-extension errors, injected
    // scripts, and common benign runtime chatter.
    ignoreErrors: [
      // Stripe.js blocked by an ad blocker / privacy extension is a client
      // environment issue, not a DrawReady bug — the signup page handles it
      // gracefully with a fallback + retry, so drop these from error alerting.
      /Stripe\.js not available/i,
      /Failed to load Stripe\.js/i,
      // Google reCAPTCHA fires internal, non-actionable runtime errors from its
      // own injected script (e.g. "Cannot read properties of undefined (reading 'p8')").
      /recaptcha/i,
      /reading 'p8'/i,
      /runtime\.sendMessage/i,
      /Tab not found/i,
      /Extension context invalidated/i,
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications.",
      /Non-Error promise rejection captured/i,
      /chrome-extension/i,
      /moz-extension/i,
      /safari-web-extension/i,
    ],
    denyUrls: [
      /gstatic\.com\/recaptcha/i,
      /google\.com\/recaptcha/i,
      /recaptcha__/i,
      /\/recaptcha\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-web-extension:\/\//i,
      /^safari-extension:\/\//i,
      /extensions\//i,
    ],
    beforeSend(event) {
      delete event.user;
      if (event.request) {
        delete event.request.data;
        delete event.request.query_string;
        if (event.request.headers) {
          for (const k of Object.keys(event.request.headers)) {
            if (SENSITIVE.test(k)) event.request.headers[k] = "[Filtered]";
          }
        }
      }
      if (event.extra) {
        for (const k of Object.keys(event.extra)) {
          if (SENSITIVE.test(k)) event.extra[k] = "[Filtered]";
        }
      }
      return event;
    },
  });
}

// Ad blockers / privacy extensions block js.stripe.com, which makes
// @stripe/stripe-js reject its own internal load promise with "Failed to load
// Stripe.js" / "Stripe.js not available". The signup page handles this
// gracefully (fallback + retry), but the library's internal promise can still
// surface as a transient unhandled rejection. Swallow ONLY those two messages
// so they never appear as uncaught errors or fire Sentry alerts — this is a
// client-environment issue, not an app bug.
if (typeof window !== "undefined") {
  window.addEventListener("unhandledrejection", (event) => {
    const msg = String(event?.reason?.message || event?.reason || "");
    if (/Stripe\.js not available|Failed to load Stripe\.js/i.test(msg)) {
      event.preventDefault();
    }
  });
}

export { Sentry };
